import {
  blobDots,
  createDotMorph,
  type DotMorph,
  type DotSet,
} from './agents-bird.thinking';

export type DotScene = (elapsed: number) => DotSet | null;

const EGG_GROW_MS = 320;
const EGG_WOBBLE_MS = 620;
const EGG_CRACK_MS = 950;
const EGG_WOBBLE_PERIOD_MS = 240;
const FINISH_MS = 760;
const FINISH_SPREAD = 0.16;
const FINISH_SWELL = 0.3;

const easeOut = (t: number) => 1 - (1 - t) ** 3;

const eggDots = (
  pixels: number,
  pitch: number,
  dark: boolean,
  size: number,
  lean: number
) =>
  blobDots(
    [
      { x: 0.5 + lean, y: 0.63, radius: 0.17 * size, tone: 0.6 },
      { x: 0.5 + lean * 1.6, y: 0.5, radius: 0.13 * size, tone: 0.5 },
    ],
    pixels,
    pitch,
    dark
  );

export const hatchScene = (
  bird: DotSet,
  pixels: number,
  pitch: number,
  dark: boolean
): DotScene => {
  let crack: DotMorph | null = null;
  return (elapsed) => {
    if (elapsed < EGG_GROW_MS) {
      return eggDots(pixels, pitch, dark, easeOut(elapsed / EGG_GROW_MS), 0);
    }
    const wobbleElapsed = elapsed - EGG_GROW_MS;
    if (wobbleElapsed < EGG_WOBBLE_MS) {
      const fade = 1 - wobbleElapsed / EGG_WOBBLE_MS;
      const lean =
        0.03 *
        fade *
        Math.sin((wobbleElapsed / EGG_WOBBLE_PERIOD_MS) * Math.PI * 2);
      return eggDots(pixels, pitch, dark, 1, lean);
    }
    const crackElapsed = wobbleElapsed - EGG_WOBBLE_MS;
    if (crackElapsed >= EGG_CRACK_MS) return null;
    crack ??= createDotMorph(eggDots(pixels, pitch, dark, 1, 0), bird);
    return crack(crackElapsed / EGG_CRACK_MS);
  };
};

export const finishScene = (bird: DotSet): DotScene => {
  const count = bird.x.length;
  const centreX = bird.x.reduce((sum, x) => sum + x, 0) / Math.max(1, count);
  const centreY = bird.y.reduce((sum, y) => sum + y, 0) / Math.max(1, count);
  return (elapsed) => {
    const progress = elapsed / FINISH_MS;
    if (progress >= 1) return null;
    const burst = Math.sin(Math.PI * progress);
    return {
      x: bird.x.map((x) => x + (x - centreX) * FINISH_SPREAD * burst),
      y: bird.y.map((y) => y + (y - centreY) * FINISH_SPREAD * burst),
      r: bird.r.map((r) => r * (1 + FINISH_SWELL * burst)),
    };
  };
};

export const morphScene = (
  from: DotSet,
  to: DotSet,
  durationMs: number
): DotScene => {
  const morph = createDotMorph(from, to);
  return (elapsed) =>
    elapsed >= durationMs ? null : morph(elapsed / durationMs);
};
